import { useCallback, useState } from "react";
import Decimal from "decimal.js";
import {
  CreateDemandForecastParams,
  DEMAND_FORECAST_CADENCE_DISPLAY_NAMES,
  HydratedDemandForecast,
  Permission,
  UpdateDemandForecastParams,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { DemandForecastEditorSheet } from "./DemandForecastEditorSheet";
import { DemandForecastCalendarView } from "./DemandForecastCalendarView";
import { useHasPermission } from "@/store/authStore";
import { useDemandForecastsStore } from "./demandForecastsStore";
import { formatMoney, formatQuantityWithUnit } from "./utils";

const PAGE_SIZE = 50;

function getWeightedRevenue(forecast: HydratedDemandForecast): string {
  const quantity = new Decimal(forecast.quantityPerPeriod || 0);
  const unitPrice = new Decimal(forecast.unitPrice || 0);
  const probability = new Decimal(forecast.opportunity?.probability ?? 0).div(
    100,
  );
  return quantity.mul(unitPrice).mul(probability).toString();
}

function getMobileForecastSummary(forecast: HydratedDemandForecast): string {
  const probability = forecast.opportunity
    ? `${forecast.opportunity.probability}% opp`
    : "No opp %";
  return [
    formatQuantityWithUnit(forecast.quantityPerPeriod, forecast.quantityUnit),
    `${formatMoney(forecast.unitPrice)} / unit`,
    probability,
    `${formatMoney(getWeightedRevenue(forecast))} weighted`,
  ].join(" | ");
}

export default function DemandForecastsController() {
  const {
    demandForecasts,
    demandForecastLoadingStates,
    listLoading,
    listError,
    fetchDemandForecasts,
    createDemandForecast,
    updateDemandForecast,
    deleteDemandForecast,
  } = useDemandForecastsStore();

  const canEdit = useHasPermission(Permission.DEMANDFORECAST_EDIT);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");
  const [activeTab, setActiveTab] = useState("calendar");
  const [calendarRefreshToken, setCalendarRefreshToken] = useState(0);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [editingForecastId, setEditingForecastId] = useState<string | null>(
    null,
  );
  const [forecastToDelete, setForecastToDelete] =
    useState<HydratedDemandForecast | null>(null);

  const {
    entitiesArray: forecastsArray,
    handleSearch: baseHandleSearch,
    setDisplayedEntityIds,
  } = useEntitySearch({
    entities: demandForecasts,
    fetchEntities: async (params) => {
      const result = await fetchDemandForecasts({
        search: params?.search,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setTotal(result.total);
      return result.ids;
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      baseHandleSearch(value);
    },
    [baseHandleSearch],
  );

  const refreshPage = useCallback(
    async (nextPage = page) => {
      const result = await fetchDemandForecasts({
        search: currentSearch || undefined,
        limit: PAGE_SIZE,
        offset: nextPage * PAGE_SIZE,
      });
      setTotal(result.total);
      setDisplayedEntityIds(result.ids);
    },
    [currentSearch, fetchDemandForecasts, page, setDisplayedEntityIds],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      refreshPage(newPage);
    },
    [refreshPage],
  );

  const handleConfirmDelete = async () => {
    if (!forecastToDelete) {
      return;
    }
    const result = await deleteDemandForecast(forecastToDelete.id);
    if (!result.success) {
      toast.error(result.error ?? "Failed to delete forecast");
      return;
    }
    toast.success("Forecast deleted");
    setForecastToDelete(null);
    setCalendarRefreshToken((current) => current + 1);
    await refreshPage();
  };

  const baseColumns: DataTableColumn<HydratedDemandForecast>[] = [
    {
      key: "description",
      header: "Forecast",
      className: "max-w-0 overflow-hidden",
      format: (_value, forecast) => (
        <div className="min-w-0 max-w-full overflow-hidden">
          <div className="truncate font-medium">
            {forecast.opportunity?.name ?? "No opportunity"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {forecast.description}
          </div>
          {forecast.reasoning && (
            <div className="mt-0.5 truncate text-xs italic text-muted-foreground/80">
              {forecast.reasoning}
            </div>
          )}
          <div className="mt-1 truncate text-xs text-muted-foreground sm:hidden">
            {getMobileForecastSummary(forecast)}
          </div>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      width: 110,
      className: "hidden overflow-hidden lg:table-cell",
      format: (_value, forecast) => (
        <div className="truncate">{forecast.location?.name ?? "-"}</div>
      ),
    },
    {
      key: "item",
      header: "Item",
      width: 110,
      className: "hidden overflow-hidden md:table-cell",
      format: (_value, forecast) => (
        <div className="truncate">{forecast.item?.name ?? "-"}</div>
      ),
    },
    {
      key: "quantityPerPeriod",
      header: "Qty / Period",
      width: 110,
      align: "right",
      className: "hidden sm:table-cell",
      format: (_value, forecast) =>
        formatQuantityWithUnit(
          forecast.quantityPerPeriod,
          forecast.quantityUnit,
        ),
    },
    {
      key: "unitPrice",
      header: "Unit Price",
      width: 110,
      align: "right",
      className: "hidden md:table-cell",
      format: (_value, forecast) => formatMoney(forecast.unitPrice),
    },
    {
      key: "opportunityProbability",
      header: "Opp %",
      width: 96,
      align: "right",
      className: "hidden sm:table-cell",
      format: (_value, forecast) =>
        forecast.opportunity ? `${forecast.opportunity.probability}%` : "-",
    },
    {
      key: "weightedRevenue",
      header: "Weighted",
      width: 110,
      align: "right",
      className: "hidden sm:table-cell",
      format: (_value, forecast) => formatMoney(getWeightedRevenue(forecast)),
    },
    {
      key: "cadence",
      header: "Cadence",
      width: 100,
      className: "hidden md:table-cell",
      format: (_value, forecast) =>
        DEMAND_FORECAST_CADENCE_DISPLAY_NAMES[forecast.cadence],
    },
    {
      key: "startDate",
      header: "Start",
      type: "date",
      width: 96,
      className: "hidden lg:table-cell",
    },
  ];

  const actionsColumn: DataTableColumn<HydratedDemandForecast> = {
    key: "actions",
    header: "",
    width: 60,
    format: (_value, forecast) => {
      const isDeleting =
        demandForecastLoadingStates.get(forecast.id)?.isLoadingDelete ?? false;
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            setForecastToDelete(forecast);
          }}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      );
    },
  };

  const columns = canEdit ? [...baseColumns, actionsColumn] : baseColumns;

  if (listError) {
    return (
      <div className="p-8 text-destructive">
        Error loading forecasts: {listError}
      </div>
    );
  }

  const editingForecast = editingForecastId
    ? demandForecasts.get(editingForecastId)
    : undefined;

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex w-full min-w-0 max-w-full flex-col"
    >
      <div className="flex min-w-0 flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
        <TabsList className="grid w-full min-w-0 grid-cols-2 sm:w-auto">
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="table">Table</TabsTrigger>
        </TabsList>
        {canEdit && (
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => setIsCreateSheetOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Forecast
          </Button>
        )}
      </div>

      <TabsContent
        value="calendar"
        className="m-0 w-full min-w-0 max-w-full p-3 sm:p-4"
      >
        <DemandForecastCalendarView refreshToken={calendarRefreshToken} />
      </TabsContent>

      <TabsContent
        value="table"
        className="m-0 min-h-[640px] w-full min-w-0 max-w-full overflow-hidden p-0"
      >
        <DataTable
          columns={columns}
          data={forecastsArray}
          searchPlaceholder="Search forecasts..."
          className="h-full min-w-0 [&_table]:w-full [&_table]:table-fixed"
          isLoading={listLoading && forecastsArray.length === 0}
          onClickRow={(forecast) => setEditingForecastId(forecast.id)}
          onChangeSearch={handleSearch}
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            total,
            onPageChange: handlePageChange,
          }}
        />
      </TabsContent>

      <DemandForecastEditorSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        mode="create"
        onSave={async (data: CreateDemandForecastParams) => {
          const { demandForecast, error } = await createDemandForecast(data);
          if (demandForecast) {
            setDisplayedEntityIds((prev) => [demandForecast.id, ...prev]);
            setCalendarRefreshToken((current) => current + 1);
            return true;
          }
          return error ?? "Failed to create forecast";
        }}
      />

      {editingForecast && (
        <DemandForecastEditorSheet
          open={!!editingForecastId}
          onOpenChange={(open) => !open && setEditingForecastId(null)}
          mode="edit"
          demandForecast={editingForecast}
          readOnly={!canEdit}
          onSave={async (data: UpdateDemandForecastParams) => {
            const { demandForecast, error } = await updateDemandForecast(
              editingForecast.id,
              data,
            );
            if (demandForecast) {
              setCalendarRefreshToken((current) => current + 1);
              await refreshPage();
              return true;
            }
            return error ?? "Failed to update forecast";
          }}
        />
      )}

      <DeleteConfirmationDialog
        open={!!forecastToDelete}
        onOpenChange={(open) => !open && setForecastToDelete(null)}
        onConfirm={handleConfirmDelete}
        entityType="Demand Forecast"
        itemName={forecastToDelete?.description}
      />
    </Tabs>
  );
}
